import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, UtensilsCrossed, Facebook, Instagram, Twitter } from 'lucide-react';
import { useCart } from '../context/CartContext';
import VoiceChatbot from './VoiceChatbot';
import { NAV_LINKS } from '../constants';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';

export const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isUrdu = i18n.language === 'ur';

  const toggleLanguage = () => {
    const newLang = isUrdu ? 'en' : 'ur';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ur' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  // Close mobile menu on route change
  React.useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-mocha-light overflow-x-hidden">
      {/* Sticky Navbar */}
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/logos/android-chrome-192x192.png"
              alt="Zayka Palace Logo"
              className="w-12 h-12 object-contain group-hover:scale-110 transition-transform"
            />
            <span className="font-serif text-2xl font-bold text-gray-900 tracking-tight">Zayka Palace</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path));
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-semibold tracking-wide uppercase transition-colors hover:text-terracotta ${isActive ? 'text-terracotta underline decoration-2 underline-offset-4' : 'text-gray-600'
                    }`}
                >
                  {t(link.name)}
                </Link>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Link to="/order" className="relative p-2 hover:bg-gray-100 rounded-full transition-colors">
              <ShoppingBag className="w-6 h-6 text-gray-700" />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-terracotta text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-white">
                  {cartCount}
                </span>
              )}
            </Link>

            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-bold text-gray-700"
              title={isUrdu ? "Switch to English" : "اردو میں دیکھیں"}
            >
              <Languages size={18} className="text-terracotta" />
              <span>{isUrdu ? 'English' : 'اردو'}</span>
            </button>

            <Link to="/reservations" className="hidden sm:block bg-terracotta hover:bg-terracotta-dark text-white px-6 py-2.5 rounded-md font-medium text-sm transition-all shadow-md hover:shadow-lg">
              {t('nav.book_table')}
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-terracotta"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-b border-gray-100 absolute w-full left-0 top-20 shadow-lg py-4 px-6 flex flex-col gap-4 animate-in slide-in-from-top-5">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-medium py-2 border-b border-gray-50 ${isActive ? 'text-terracotta' : 'text-gray-600'
                    }`}
                >
                  {t(link.name)}
                </Link>
              );
            })}
            <Link to="/reservations" className="bg-terracotta text-white py-3 rounded-md text-center font-bold mt-2">
              {t('nav.book_table')}
            </Link>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] text-white pt-16 pb-8">
        <div className="container mx-auto px-4 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="mb-6">
              <img
                src="/logos/android-chrome-192x192.png"
                alt="Zayka Palace Logo"
                className="w-16 h-16 object-contain mb-4"
              />
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a href="#" className="bg-white/10 p-2 rounded-full hover:bg-terracotta transition-colors"><Facebook size={18} /></a>
              <a href="#" className="bg-white/10 p-2 rounded-full hover:bg-terracotta transition-colors"><Instagram size={18} /></a>
              <a href="#" className="bg-white/10 p-2 rounded-full hover:bg-terracotta transition-colors"><Twitter size={18} /></a>
            </div>
          </div>

          <div>
            <h4 className="font-serif text-lg font-bold mb-6 text-gold">{t('footer.quick_links')}</h4>
            <ul className="space-y-3 text-gray-400">
              <li><Link to="/menu" className="hover:text-terracotta transition-colors">{t('nav.menu')}</Link></li>
              <li><Link to="/reservations" className="hover:text-terracotta transition-colors">{t('nav.reservations')}</Link></li>
              <li><Link to="/order" className="hover:text-terracotta transition-colors">{t('nav.order')}</Link></li>
              <li><Link to="/about" className="hover:text-terracotta transition-colors">{t('nav.about')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg font-bold mb-6 text-gold">{t('footer.contact')}</h4>
            <ul className="space-y-3 text-gray-400">
              <li>Main Boulevard Gulberg</li>
              <li>Lahore, Pakistan 54000</li>
              <li>+92 42 111-123-456</li>
              <li>hello@zaykapalace.com</li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg font-bold mb-6 text-gold">{t('footer.opening_hours')}</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex justify-between"><span>Mon - Sun:</span> <span>12pm - 12am</span></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 text-center text-gray-500 text-sm">
          {t('footer.rights')}
        </div>
      </footer>

      {/* Floating Chatbot */}
      <VoiceChatbot />
    </div>
  );
};